import { createStore } from 'vuex';
import { FacetList, facetValue } from './interfaces/Filter';
import { getDefaultFilters, getCategoriesMap } from './services/productService';

// Definiamo il tipo per lo stato del nostro store
interface ConfigurationParams {
	catalogId: string;
	storeId: string;
	langId: string;
	categoryId: string;
	earlyAccessEnabled: boolean;
	searchHostName: string;
	isSearchPage: boolean;
	completePairMode: string;
	completePairLabel: string;
	inStockFacet: string;
	categoryName?: string;
	isSeoCustomPLP: boolean;
	isSpeedyPLP: boolean;
	isSpeedyRxEnabled: boolean;
	isFlatPLP: boolean;
}

export interface RootState {
	configurationParams: ConfigurationParams;
	selectedFilters: facetValue[];
	defaultFacet: FacetList[] | null;
	categoriesMap: Map<string, string>;
	brandSeoUrlMap: Map<string, string>;
	minPriceFilter?: number;
	maxPriceFilter?: number;
	selectedPriceRange: number[];
	isSortByOpen: boolean;

}

const store = createStore<RootState>({
	state: {
		configurationParams: {
			catalogId: '',
			storeId: '',
			langId: '',
			categoryId: '',
			earlyAccessEnabled: false,
			searchHostName: '',
			isSearchPage: false,
			completePairMode: '',
			completePairLabel: '',
			inStockFacet: '',
			categoryName: '',
			isSeoCustomPLP: false,
			isSpeedyPLP: false,
			isSpeedyRxEnabled: false,
			isFlatPLP: false,
		},
		selectedFilters: [],
		defaultFacet: null,
		categoriesMap: new Map<string, string>(),
		brandSeoUrlMap: new Map<string, string>(),
		selectedPriceRange: [], // Valore iniziale
		isSortByOpen: false,

	},
	mutations: {
		setParams(state, newConfigurationParams: ConfigurationParams) {
			state.configurationParams = newConfigurationParams;
		},

		setPriceFilters(state, { minPrice, maxPrice }) {
			state.minPriceFilter = minPrice;
			state.maxPriceFilter = maxPrice;
			state.selectedPriceRange = [minPrice, maxPrice];
		},

		// Aggiungi un filtro allo stato
		ADD_FILTER(state, filter: facetValue) {
			if (!state.selectedFilters.some(f => f.value === filter.value)) {
				state.selectedFilters = [...state.selectedFilters, filter];
			}
		},
		// Rimuovi un filtro dallo stato
		REMOVE_FILTER(state, filter: facetValue) {
			state.selectedFilters = state.selectedFilters.filter(
				f => f.value !== filter.value
			);
		},

		SET_DEFAULT_FACET(state, facetData) {
			if (state.defaultFacet === null) { // Inizializza solo se non è stato già fatto
				state.defaultFacet = facetData;
			}
		},

		RESET_SELECTED_FILTERS(state) {
			state.selectedFilters = [];
			store.dispatch('updateSelectedPriceRange', [state.minPriceFilter, state.maxPriceFilter]);
		},

		SET_CATEGORIES_MAP(state, categoriesMap: Map<string, string>) {
			state.categoriesMap = categoriesMap;
		},

		SET_BRAND_SEO_URL(state, { key, value }) {
			state.brandSeoUrlMap = {
				...state.brandSeoUrlMap,
				[key]: value,
			};
		},

		setSelectedPriceRange(state, newRange) {
			state.selectedPriceRange = newRange
		},

		SET_SORT_BY_MODAL_OPENED(state, value: boolean) {
			state.isSortByOpen = value;
		}


	},
	actions: {
		toggleFilter({ commit, state }, filter: facetValue) {
			const isPriceFilter = filter.value.includes('price_USD');

			// Controlla se il filtro è già selezionato
			const isFilterSelected = state.selectedFilters.some(
				f => f.value === filter.value
			);

			if (isFilterSelected) {
				// Se il filtro è selezionato, rimuovilo
				commit('REMOVE_FILTER', filter);
				if (isPriceFilter) {
					const resetRange = [state.minPriceFilter, state.maxPriceFilter]
					store.dispatch('updateSelectedPriceRange', resetRange);
				}
			} else {
				if (isPriceFilter) {
					// Se è un filtro di prezzo, rimuovi tutti i filtri di prezzo esistenti
					const priceFilters = state.selectedFilters.filter(f =>
						f.value.includes('price_USD')
					);
					priceFilters.forEach(priceFilter => {
						commit('REMOVE_FILTER', priceFilter);
					});
				}
				// Aggiungi il filtro
				commit('ADD_FILTER', filter);
			}
		}
		,
		async initializeFacet({ commit, state }) {
			if (state.defaultFacet === null) { // Inizializza solo se il facet è ancora null
				const facetData = await getDefaultFilters({
					...state.configurationParams,
					pageNumber: 1,
					pageSize: 1,
				});
				commit('SET_DEFAULT_FACET', facetData.facets);
			}
		},
		async initializeCategories({ commit }, categoriesIdentifier: string[]) {
			const categoriesMap = await getCategoriesMap(categoriesIdentifier);
			commit('SET_CATEGORIES_MAP', categoriesMap);
		},
		updateSelectedPriceRange({ commit }, range) {
			commit('setSelectedPriceRange', range);
		},
		setSortByOpened({ commit }, value: boolean) {
			commit('SET_SORT_BY_MODAL_OPENED', value);
		}

	},
	getters: {
		selectedFilters: (state): facetValue[] => state.selectedFilters,
		defaultFacet: (state) => state.defaultFacet,
		configurationParams: (state) => state.configurationParams,
		categoriesMap: (state) => state.categoriesMap,
		brandSeoUrlMap: (state) => state.brandSeoUrlMap,
		selectedPriceRange: (state) => state.selectedPriceRange,
		isSortByOpen: (state) => state.isSortByOpen,
		maxPriceFilter: (state) => state.maxPriceFilter,
		minPriceFilter: (state) => state.minPriceFilter,
	}

});

export default store;


